import React from 'react';
import { motion } from 'framer-motion';
import { Phone } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
type Tier = {
  id: string;
  label: string;
  name: string;
  price: string;
  tagline: string;
  popular: boolean;
  headerBg: string;
  headerText: string;
  btnBg: string;
  valueColor: string;
};
const tiers: Tier[] = [{
  id: 'pilot',
  label: 'Entry',
  name: 'PILOT',
  price: '$615',
  tagline: 'Get the foundation right. Site, local SEO, phone and AI support.',
  popular: false,
  headerBg: 'bg-[#3A1D7A]',
  headerText: 'text-white',
  btnBg: 'bg-[#3A1D7A] hover:bg-[#2F1763] text-white',
  valueColor: 'text-[#3A1D7A]'
}, {
  id: 'copilot',
  label: 'Starter',
  name: 'CO-PILOT',
  price: '$1,235',
  tagline: 'Start driving demand. Paid ads, social and content added on top.',
  popular: false,
  headerBg: 'bg-[#4C2596]',
  headerText: 'text-white',
  btnBg: 'bg-[#4C2596] hover:bg-[#3E1E7C] text-white',
  valueColor: 'text-[#4C2596]'
}, {
  id: 'autopilot',
  label: 'Growth',
  name: 'AUTOPILOT',
  price: '$2,780',
  tagline: 'Never miss a lead. Dedicated MR line, estimates, full pipelines.',
  popular: true,
  headerBg: 'bg-[#22D3EE]',
  headerText: 'text-[#0F0A29]',
  btnBg: 'bg-[#22D3EE] hover:bg-[#0FB8D4] text-[#0F0A29]',
  valueColor: 'text-[#0E7490]'
}, {
  id: 'command',
  label: 'Full System',
  name: 'COMMAND CENTER',
  price: '$5,355',
  tagline: 'The whole operation. White-label MISTR and advanced analytics.',
  popular: false,
  headerBg: 'bg-[#0F0A29]',
  headerText: 'text-white',
  btnBg: 'bg-[#0F0A29] hover:bg-[#1C1342] text-white',
  valueColor: 'text-[#0F0A29]'
}];
type FeatureGroup = {
  group: string;
  note?: string;
  features: {
    name: string;
    values: string[];
  }[];
};
const featureMatrix: FeatureGroup[] = [{
  group: 'Website',
  note: '50 credits a month roughly covers steady site edits, or one new landing page. 70 credits builds a whole new multi-page website. A few credits over your monthly amount are absorbed at no charge — extra beyond that is sold in a simple $40 block, never billed penny-by-penny.',
  features: [{
    name: 'Mobile Responsive Website',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'First Website Included',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Hosting Included',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Website Backups (Real-Time Version History, Restore Any Past Version)',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Page Edits and Updates (Credit-Metered, See Credits Below)',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Additional Website or Landing Page, Standard Hosting',
    values: ['1-3 Sites: $39/Site, 4+ Sites: $25/Site', '1-3 Sites: $39/Site, 4+ Sites: $25/Site', '1-3 Sites: $39/Site, 4+ Sites: $25/Site', '1-3 Sites: $39/Site, 4+ Sites: $25/Site']
  }, {
    name: 'Additional Website or Landing Page, Advanced Hosting',
    values: ['$97/Site', '$97/Site', '$97/Site', '$97/Site']
  }, {
    name: 'WordPress Hosting (Not Included, Available on Request)',
    values: ['$20/Month', '$20/Month', '$20/Month', '$20/Month']
  }, {
    name: 'Credits Included Per Month (Resets Monthly, Does Not Roll Over)',
    values: ['50', '100', '200', '400']
  }, {
    name: 'Extra Credits, Grace Before Overage',
    values: ['5-10', '5-10', '5-10', '5-10']
  }, {
    name: 'Additional Credits Beyond Grace',
    values: ['$40 per 25 Credits', '$40 per 25 Credits', '$40 per 25 Credits', '$40 per 25 Credits']
  }]
}, {
  group: 'Get Found — Local SEO',
  features: [{
    name: 'On-Page SEO Audit',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Sitemap Submission',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Directory Listings, Yext Partnership (200+ Directories, Live in Seconds)',
    values: ['$79/Month', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Listing Analytics (Impressions, Clicks, Calls and Directions)',
    values: ['With Yext Add-On', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Google Business Profile Setup and Optimization',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Google Business Profile Posts, Twice Monthly',
    values: ['$149/Month', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Review Outreach Automation (Boosts Local Ranking)',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Automatic Review Responses (AI), Written in Your Voice',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }]
}, {
  group: 'Lead Generation — Paid Ads (PPC)',
  features: [{
    name: 'Google or Meta Ads Managed for You',
    values: ['No', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Ad Networks (Google, Meta or LinkedIn)',
    values: ['No', 'Pick 1', 'Pick Up to 2', 'Pick Up to 3']
  }, {
    name: 'Managed Ad Spend Included, Up To',
    values: ['No', '$3,000/Mo', '$10,000/Mo', '$25,000/Mo']
  }, {
    name: 'Campaign Landing Pages (Uses Website Credits)',
    values: ['No', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Ad Creative Created for You (Copy, Images and Video)',
    values: ['No', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Click Fraud Protection (ClickCease, or Your Existing Tool)',
    values: ['No', '$99/Month Add-On', '$99/Month Add-On', '$99/Month Add-On']
  }, {
    name: 'Retargeting (Ads to People Who Already Visited Your Site)',
    values: ['No', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Call Tracking (Which Ad Made the Phone Ring)',
    values: ['No', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Call Tracking Number Pools (Know Which Exact Keyword or Ad Drove the Call)',
    values: ['No', 'No', 'Yes', 'Yes']
  }, {
    name: 'TrueConversion Analytics',
    values: ['No', 'No', 'Yes', 'Yes']
  }]
}, {
  group: 'Content',
  features: [{
    name: 'Blog Posts Per Month',
    values: ['No', '1', '2', '4']
  }, {
    name: 'Press Releases Per Month (Content Written for You, You Handle Submission)',
    values: ['No', 'No', '1', '2']
  }, {
    name: 'Off-Site Articles Per Month',
    values: ['No', 'No', '2', '4']
  }]
}, {
  group: 'Social Media',
  features: [{
    name: 'Social Content Created and Posted',
    values: ['No', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Networks Posted To',
    values: ['Facebook, Instagram, Google Business Profile, LinkedIn, TikTok, YouTube, Pinterest, Threads, Bluesky', 'Facebook, Instagram, Google Business Profile, LinkedIn, TikTok, YouTube, Pinterest, Threads, Bluesky', 'Facebook, Instagram, Google Business Profile, LinkedIn, TikTok, YouTube, Pinterest, Threads, Bluesky', 'Facebook, Instagram, Google Business Profile, LinkedIn, TikTok, YouTube, Pinterest, Threads, Bluesky']
  }, {
    name: 'Posts Per Day, Done For You',
    values: ['No', '2', '3-4', '4-6']
  }, {
    name: 'Automated Responses to Social Comments',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Automated Responses to Social DMs and Messages',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Social Listening',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Reviews Automatically Posted to Social Media (Written Reviews Only)',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }]
}, {
  group: 'Email Marketing',
  features: [{
    name: 'Email and Text Marketing Tools',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Email Deliverability Monitoring (SPF, DKIM and DMARC Records)',
    values: ['No', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Email Verification (Catches Bad Addresses Before You Send)',
    values: ['No', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Deliverability Compliance Reports (Pass/Fail Rates, Geo Tracking)',
    values: ['No', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Email Authentication Setup and Fixes (Getting Your Records Right)',
    values: ['No', 'No', 'Yes', 'Yes']
  }, {
    name: 'AI Tools to Create Emails, Newsletters and Sequences (Self-Serve)',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }]
}, {
  group: 'Phone System',
  features: [{
    name: 'Your Own Business Phone Number',
    values: ['1', '2', '3', '8']
  }, {
    name: 'Keep Your Existing Number',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Call Recording',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Two-Way Texting',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Missed-Call Text Back',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Live Call Transfer to a Person',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Forward to Your Cell, Ring Several Phones',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'After-Hours Routing',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Voicemail With a Written Transcript',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Call Log With Every Recording',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Spam and Robocall Screening',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Voicemail Drop (Leave a Message Without Waiting)',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Whisper (Hear Where the Call Came From First)',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Extra Phone Number, Per Month',
    values: ['$3.45', '$3.45', '$3.45', '$3.45']
  }, {
    name: 'Toll-Free Number, Per Month',
    values: ['$6.45', '$6.45', '$6.45', '$6.45']
  }]
}, {
  group: 'AI Phone Attendant',
  features: [{
    name: 'Answers 24/7, No Caller Hits Voicemail',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Books the Appointment Onto Your Calendar',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Answers Hours, Service Area, Common Questions',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Qualifies the Caller Before It Rings You',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Texts You a Summary After Every Call',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Dedicated MR Line, 24/7 AI',
    values: ['No', 'No', 'Yes', 'Yes']
  }, {
    name: 'Reschedules an Appointment by Text, Confirms and Updates Automatically',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }]
}, {
  group: 'AI Support — Chat and Follow-Up',
  features: [{
    name: 'Website Chat That Answers on the Spot',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Books an Appointment Right Inside the Chat',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: '24/7 Lead Capture',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Every New Lead Answered in Seconds',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Form Auto-Follow-Up',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Old Leads Woken Back Up',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }]
}, {
  group: 'Reviews & Reputation',
  features: [{
    name: 'Review Automation',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Reputation Management',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Review Replies Written in Your Voice (Reviews AI)',
    values: ['No', 'No', 'Yes', 'Yes']
  }, {
    name: 'Dispute a Fake or Unfair Google Review',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Reputation Reports',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Review Widget on Your Site',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Video Testimonials (Built-In Request Tool, No Extra Cost)',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }]
}, {
  group: 'One Inbox for Every Message',
  features: [{
    name: 'Text Messages and Email',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Facebook and Instagram Messages',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Google Business Profile Messages',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'TikTok, LinkedIn and Threads Messages',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'WhatsApp',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Website Chat',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Missed Calls and Voicemails',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Email and Text Marketing',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }]
}, {
  group: 'Your Customer Database (CRM)',
  features: [{
    name: 'Every Lead, Customer and Job in One Place',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: '1Dash.Pro Account of Your Own',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Job / Sales / Opportunity Pipeline (See What Stage Every Lead Is In)',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Full History on Every Contact',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Online Booking Calendar (Service, Property or Equipment Scheduling)',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Estimates, Invoices and Text-to-Pay',
    values: ['No', 'No', 'Yes', 'Yes']
  }, {
    name: 'Mobile App for the Field',
    values: ['Yes', 'Yes', 'Yes', 'Yes']
  }, {
    name: 'Team Seats Included',
    values: ['5', '10', '20', '50']
  }, {
    name: 'Pipelines and Automation Built for You',
    values: ['No', 'No', 'Yes', 'Full']
  }]
}, {
  group: 'Included Usage',
  features: [{
    name: 'Calling, Texting and AI Included Every Month, Covers Phone/Chat/AI/WhatsApp (Resets Monthly, Does Not Roll Over)',
    values: ['$15', '$30', '$60', '$160']
  }]
}, {
  group: 'MISTR & Wayland',
  features: [{
    name: 'Personalized / White-Label MISTR',
    values: ['No', 'No', 'No', 'Yes']
  }, {
    name: 'Monthly Report and Priority Support',
    values: ['No', 'No', 'Yes', 'Yes']
  }, {
    name: 'Advanced Analytics Dashboard',
    values: ['No', 'No', 'No', 'Yes']
  }]
}];
const GRID = 'grid grid-cols-[minmax(340px,1.5fr)_repeat(4,1fr)]';
/** Thin vertical column divider, subtle light purple. */
const VDIV = 'border-l border-[#DDD3F2]';
function renderValue(val: string, valueColor: string) {
  if (val === 'Yes') {
    return <span className={`font-bold text-sm ${valueColor}`} data-id="element-1905" data-mp="38ajhkodlkaa1">Yes</span>;
  }
  if (val === 'No') {
    return <span className="font-bold text-[16px] leading-none text-gray-500" aria-label="Not included" data-id="element-1906" data-mp="3d5fike0ta7za">
        &mdash;
      </span>;
  }
  return <span className="text-gray-700 font-medium text-[13px] leading-snug" data-id="element-1907" data-mp="woho0kyzk2iy">{val}</span>;
}
export function Pricing() {
  return <main className="min-h-screen bg-dark-bg text-white font-inter" data-id="element-1908" data-mp="1bg9pbt1y3g1o">
      <Helmet data-id="element-1909" data-mp="16gey1oe9cog3">
        <title data-id="element-1910" data-mp="16fvsl75snsiy">Pricing — MindStream.ing | 4 Plans, From $615/mo</title>
        <meta name="description" content="Pick your plan. Pilot ($615), Co-Pilot ($1,235), Autopilot ($2,780), Command Center ($5,355). White Glove Onboarding $997 with a real website build. 3-month minimum term." data-id="element-1911" data-mp="iqkobfp8jg02" />
      </Helmet>

      {/* Hero */}
      <section className="relative pt-32 pb-20 px-6 bg-hero-gradient overflow-hidden text-center" data-id="element-1912" data-mp="no30deyjubca">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-purple/20 rounded-full blur-[120px] opacity-30 pointer-events-none" data-id="element-1913" data-mp="234tncpdjnqg4" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-teal/20 rounded-full blur-[100px] opacity-20 pointer-events-none" data-id="element-1914" data-mp="1z382agquw5uu" />

        <div className="relative z-10 max-w-4xl mx-auto" data-id="element-1915" data-mp="10ryhqntpgo2x">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-1916" data-mp="10qdod1j4nb6n">
            <span className="font-poppins font-semibold text-xs tracking-[0.15em] uppercase text-[#22D3EE] mb-4 block" data-id="element-1917" data-mp="19rbqtfdx3an4">
              Pricing
            </span>
            <h1 className="font-poppins font-extrabold text-5xl md:text-6xl lg:text-7xl text-white leading-[1.1] mb-6" data-id="element-1918" data-mp="i0xlw8nvt8nl">
              Pick the plan that fits <br className="hidden md:block" data-id="element-1919" data-mp="m1hklgo3skbu" />
              <span className="text-gradient" data-id="element-1920" data-mp="qtis6pabtmvj">your business.</span>
            </h1>
            <p className="text-xl text-steel max-w-2xl mx-auto leading-relaxed" data-id="element-1921" data-mp="qvlnnou9087n">
              Every plan = MindStream.ing — your business, running itself. Upgrade any time.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Matrix */}
      <section className="py-16 lg:py-24 px-4 lg:px-6 bg-white text-gray-900" data-id="element-1922" data-mp="3mytagcifg0cp">
        <div className="max-w-[1500px] mx-auto" data-id="element-1923" data-mp="3i1ba3aih6tgb">
          {/* Desktop table */}
          <div className="hidden lg:block" data-id="element-1924" data-mp="3dy550673n8bf">
            <div className="shadow-2xl border border-gray-200 bg-white" data-id="element-1925" data-mp="3dwts9gvoeq86">
              {/* Tier headers */}
              <div className={GRID} data-id="element-1926" data-mp="391o8anlu3lmq">
                <div className="p-8 flex flex-col justify-end bg-white" data-id="element-1927" data-mp="3410i245fj8tk">
                  <p className="font-poppins text-gray-600 italic text-lg leading-snug" data-id="element-1928" data-mp="342bo6amcksnu">
                    Choose a plan that&rsquo;s right
                  </p>
                  <p className="font-poppins text-gray-600 italic text-lg leading-snug mb-5" data-id="element-1929" data-mp="2ub76v4978vpu">
                    for your business.
                  </p>
                  <p className="text-xs text-gray-600 leading-relaxed" data-id="element-1930" data-mp="2l9ptvur39a5d">
                    Prices shown are card price. Paying by ACH or check? Email{' '}
                    <a href="mailto:results@getadroi.com" className="font-semibold text-[#4C2596] hover:text-[#3A1D7A] underline decoration-[#4C2596]/30" data-id="element-1931" data-mp="2gboa17lu96su">
                      results@getadroi.com
                    </a>{' '}
                    for details.
                  </p>
                  <p className="text-xs text-gray-600 font-semibold mt-3" data-id="element-1932" data-mp="403csfwapnhj">
                    3-month minimum term.
                  </p>
                </div>

                {tiers.map(tier => <div key={tier.id} className={`${tier.headerBg} ${tier.headerText} p-6 text-center relative border-l border-white/20`} data-id="element-1933" data-mp="3q8cqvg0re4s4">
                    {tier.popular && <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-[#0F0A29]/15 px-3 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase" data-id="element-1934" data-mp="3gijck798fbk6">
                        Most Popular
                      </div>}
                    <div className={`text-[11px] font-bold tracking-widest uppercase opacity-90 mb-1 ${tier.popular ? 'mt-4' : ''}`} data-id="element-1935" data-mp="2nv05fozrb1g6">
                      {tier.label}
                    </div>
                    <div className="font-poppins font-extrabold text-lg tracking-tight mb-4" data-id="element-1936" data-mp="2summ68esmztq">
                      {tier.name}
                    </div>
                    <div className="font-poppins font-extrabold text-4xl xl:text-5xl leading-none mb-1" data-id="element-1937" data-mp="36st8cradrbdk">
                      {tier.price}
                    </div>
                    <div className="text-xs opacity-95 font-medium" data-id="element-1938" data-mp="3gkguqds2y286">per month</div>
                  </div>)}
              </div>

              {/* Taglines */}
              <div className={`${GRID} bg-gray-50 border-b border-gray-200`} data-id="element-1939" data-mp="3pkvkbpc2u3hh">
                <div className="px-6 py-4 flex items-center" data-id="element-1940" data-mp="3piswnbyecmfx">
                  <p className="font-poppins font-bold text-xs tracking-widest uppercase text-gray-500" data-id="element-1941" data-mp="3lkld69cjt711">
                    What Each Plan Is For
                  </p>
                </div>
                {tiers.map(tier => <div key={tier.id} className={`p-4 text-center ${VDIV}`} data-id="element-1942" data-mp="3t51jq878dkzm">
                    <p className="text-xs text-gray-600 leading-snug" data-id="element-1943" data-mp="awsk2spv9ew3">{tier.tagline}</p>
                  </div>)}
              </div>

              {/* Onboarding */}
              <div className={`${GRID} bg-white border-b border-gray-200`} data-id="element-1944" data-mp="3ef2oad728nnu">
                <div className="px-6 py-4" data-id="element-1945" data-mp="3aczavj0tz8zx">
                  <p className="font-poppins font-bold text-sm text-gray-900" data-id="element-1946" data-mp="3ae0nhrxhw3jr">
                    White Glove Onboarding, One-Time
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-1947" data-mp="3jcm1ravxoweq">
                    Includes a real website build during onboarding.
                  </p>
                </div>
                {tiers.map(tier => <div key={tier.id} className={`px-4 py-4 flex items-center justify-center ${VDIV}`} data-id="element-1948" data-mp="2qp88pnvcwkzw">
                    <span className="font-poppins font-bold text-lg text-gray-900" data-id="element-1949" data-mp="1ax4e5v4difgg">$997</span>
                  </div>)}
              </div>

              {/* Feature groups */}
              {featureMatrix.map(group => <div key={group.group} data-id="element-1950" data-mp="125vqpfjuwkwr">
                  {/* Sticky section bar — section name + each tier in its own color */}
                  <div className={`${GRID} sticky top-20 z-20 shadow-md`} data-id="element-1951" data-mp="1pv6iw8n556za">
                    <div className="px-6 py-3 bg-[#7C3AED] flex items-center" data-id="element-1952" data-mp="1tup65b2fpu5a">
                      <h3 className="font-poppins font-bold text-white text-base tracking-wide uppercase" data-id="element-1953" data-mp="1fx0ki10jmiz6">
                        {group.group}
                      </h3>
                    </div>
                    {tiers.map(tier => <div key={tier.id} className={`${tier.headerBg} ${tier.headerText} px-4 py-3 text-center border-l border-white/20`} data-id="element-1954" data-mp="4fr2lix2byzz">
                        <div className="font-poppins font-bold text-[11px] tracking-widest uppercase leading-none" data-id="element-1955" data-mp="3llj7b8f32c7x">
                          {tier.name}
                        </div>
                        <div className="font-poppins font-extrabold text-base leading-tight mt-1" data-id="element-1956" data-mp="3cpa5xo7jp3j5">
                          {tier.price}
                          <span className="font-semibold text-[10px] opacity-90" data-id="element-1957" data-mp="32sp050npboe9">/mo</span>
                        </div>
                      </div>)}
                  </div>

                  {group.features.map((feature, fIdx) => <div key={feature.name} className={`${GRID} ${fIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} border-b border-gray-100`} data-id="element-1958" data-mp="2tssdt5osb1md" data-mpi={fIdx}>
                      <div className="px-6 py-3 text-sm text-gray-700 font-medium leading-snug" data-id="element-1959" data-mp="n19hn1kgl98r">
                        {feature.name}
                      </div>
                      {feature.values.map((val, vIdx) => <div key={tiers[vIdx].id} className={`px-4 py-3 flex items-center justify-center text-center ${VDIV}`} data-id="element-1960" data-mp="zjd9fvn0suhe" data-mpi={vIdx}>
                          {renderValue(val, tiers[vIdx].valueColor)}
                        </div>)}
                    </div>)}

                  {group.note && <div className={GRID} data-id="element-1961" data-mp="gmc429pk0scx">
                      <div className="col-span-5 px-6 py-4 bg-[#F5F3FF] border-b border-gray-200" data-id="element-1962" data-mp="lknbyewagssi">
                        <p className="text-xs text-gray-700 leading-relaxed max-w-4xl" data-id="element-1963" data-mp="pm8w8lb5k9cq">
                          {group.note}
                        </p>
                      </div>
                    </div>}
                </div>)}

              {/* CTA row */}
              <div className={`${GRID} bg-gray-100 border-t-2 border-gray-200`} data-id="element-1964" data-mp="1zk8lufv3algb">
                <div className="p-6" data-id="element-1965" data-mp="1zmb9wucom4ie" />
                {tiers.map(tier => <div key={tier.id} className={`p-4 flex items-center justify-center ${VDIV}`} data-id="element-1966" data-mp="1umoxuo8laub6">
                    <Link to="/contact" className={`w-full py-3 rounded-md font-poppins font-bold text-sm text-center transition-colors duration-200 ease-out ${tier.btnBg} shadow-md`} data-id="element-1967" data-mp="24isa4mx5o9bu">
                      Get Started
                    </Link>
                  </div>)}
              </div>
            </div>
          </div>

          {/* Mobile stacked */}
          <div className="lg:hidden space-y-6" data-id="element-1968" data-mp="2nabb6o7mddb8">
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-5" data-id="element-1969" data-mp="319aztvv4ts7e">
              <p className="text-xs text-gray-700 leading-relaxed" data-id="element-1970" data-mp="317802k46gkph">
                Prices shown are card price. Paying by ACH or check? Email{' '}
                <a href="mailto:results@getadroi.com" className="font-semibold text-[#4C2596] underline" data-id="element-1971" data-mp="3fxcf1pw0m8xt">
                  results@getadroi.com
                </a>{' '}
                for details.
              </p>
              <p className="text-xs text-gray-700 font-semibold mt-2" data-id="element-1972" data-mp="3bufqzjnphkmh">3-month minimum term.</p>
              <p className="text-xs text-gray-700 mt-2" data-id="element-1973" data-mp="2x5s03qot1z2v">
                White Glove Onboarding: $997 one-time on every plan, includes a real website build.
              </p>
            </div>

            {tiers.map((tier, tierIdx) => <div key={tier.id} className={`rounded-xl overflow-hidden bg-white border ${tier.popular ? 'border-[#22D3EE] ring-2 ring-[#22D3EE]/40 shadow-xl' : 'border-gray-200 shadow-md'}`} data-id="element-1974" data-mp="2o64vkw0s4d5l" data-mpi={tierIdx}>
                <div className={`${tier.headerBg} ${tier.headerText} p-6 text-center relative`} data-id="element-1975" data-mp="2j5h82kbreeg4">
                  {tier.popular && <div className="absolute top-2 right-2 bg-[#0F0A29]/15 px-3 py-1 rounded-full text-[10px] font-bold tracking-wider uppercase" data-id="element-1976" data-mp="jz3gzcsnv2mz">
                      Most Popular
                    </div>}
                  <div className="text-[11px] font-bold tracking-widest uppercase opacity-90 mb-1" data-id="element-1977" data-mp="az6mgujlb9k0">
                    {tier.label}
                  </div>
                  <div className="font-poppins font-extrabold text-2xl mb-3" data-id="element-1978" data-mp="17mjwfeutv2wi">{tier.name}</div>
                  <div className="font-poppins font-extrabold text-5xl mb-1" data-id="element-1979" data-mp="13kydpcvm3uee">{tier.price}</div>
                  <div className="text-sm opacity-95" data-id="element-1980" data-mp="yq2mug243d2v">per month</div>
                  <p className="text-sm opacity-95 mt-4 leading-relaxed" data-id="element-1981" data-mp="to3hx1qq483t">{tier.tagline}</p>
                </div>

                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gray-50" data-id="element-1982" data-mp="3jfryylbfkkrv">
                  <span className="text-sm text-gray-700 font-medium" data-id="element-1983" data-mp="3eh6z4n03c33y">
                    White Glove Onboarding, One-Time
                  </span>
                  <span className="font-poppins font-bold text-gray-900" data-id="element-1984" data-mp="399gzcgyf1hlk">$997</span>
                </div>

                {featureMatrix.map(group => <div key={group.group} data-id="element-1985" data-mp="3e7a558vt49lb">
                    <div className="px-5 py-3 bg-[#7C3AED] sticky top-16 z-10" data-id="element-1986" data-mp="3j0bmroneen8l">
                      <h4 className="font-poppins font-bold text-sm text-white tracking-wide uppercase" data-id="element-1987" data-mp="3n1x5vrqizxr8">
                        {group.group}
                      </h4>
                    </div>
                    {group.features.map((feature, fIdx) => <div key={feature.name} className={`px-5 py-3 flex items-start justify-between gap-4 ${fIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} border-b border-gray-100`} data-id="element-1988" data-mp="v8clpikrl1b" data-mpi={fIdx}>
                        <span className="text-sm text-gray-700 font-medium leading-snug" data-id="element-1989" data-mp="27l6f068etyi4">
                          {feature.name}
                        </span>
                        <div className="shrink-0 text-right max-w-[45%]" data-id="element-1990" data-mp="8cy6hud91cpn">
                          {renderValue(feature.values[tierIdx], tier.valueColor)}
                        </div>
                      </div>)}
                    {group.note && <div className="px-5 py-4 bg-[#F5F3FF] border-b border-gray-200" data-id="element-1991" data-mp="3qjcg5h4hj0i8">
                        <p className="text-xs text-gray-700 leading-relaxed" data-id="element-1992" data-mp="3lkhrvznr0jqq">{group.note}</p>
                      </div>}
                  </div>)}

                <div className="p-5 bg-gray-100" data-id="element-1993" data-mp="3cjw45o6dqog">
                  <Link to="/contact" className={`block w-full py-4 rounded-md font-poppins font-bold text-center transition-colors duration-200 ease-out ${tier.btnBg} shadow-md`} data-id="element-1994" data-mp="3vhmdnydomd70">
                    Get Started with {tier.name}
                  </Link>
                </div>
              </div>)}
          </div>

          {/* Footer note */}
          <div className="text-center mt-16 max-w-2xl mx-auto" data-id="element-1995" data-mp="1nbtz809pr6xl">
            <p className="text-gray-600 mb-2" data-id="element-1996" data-mp="18ebct4kid5tb">
              All plans include White Glove Onboarding for $997, with a real website built during
              onboarding.
            </p>
            <p className="text-gray-600 mb-6" data-id="element-1997" data-mp="212qpvlk2xe20">3-month minimum term on all plans.</p>
            <Link to="/contact" className="inline-flex items-center gap-2 text-[#4C2596] font-semibold hover:text-[#3A1D7A] transition-colors duration-200 ease-out text-lg" data-id="element-1998" data-mp="214tga6p59979">
              Not sure which? Book a 15-min strategy call →
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-32 px-6 bg-hero-gradient overflow-hidden flex items-center justify-center text-center border-t border-white/10" data-id="element-1999" data-mp="2k0jk7zgn0fns">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-purple/20 rounded-full blur-[120px] opacity-30 pointer-events-none" data-id="element-2000" data-mp="3qsbxqdkqsrhb" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-teal/20 rounded-full blur-[100px] opacity-20 pointer-events-none" data-id="element-2001" data-mp="3vpu9scu23mtj" />

        <div className="relative z-10 max-w-3xl mx-auto" data-id="element-2002" data-mp="4etxiu82yews">
          <h2 className="font-poppins font-extrabold text-4xl md:text-5xl text-white mb-6" data-id="element-2003" data-mp="9a99thqfw2r3">
            Still have questions?
          </h2>
          <p className="text-xl text-steel mb-10" data-id="element-2004" data-mp="37vkjiik252k4">Talk to MR (our AI) right now</p>
          <a href="tel:760-334-6463" className="inline-flex items-center gap-3 bg-[#22D3EE] text-[#0F0A29] px-8 py-4 rounded-xl font-poppins font-bold text-xl md:text-2xl hover:bg-[#0FB8D4] transition-colors duration-200 ease-out shadow-[0_0_40px_rgba(34,211,238,0.3)] mb-8" data-id="element-2005" data-mp="3cudlbdpzf54g">
            <Phone size={28} data-id="element-2006" data-mp="x3hlixk7rie1" />
            760-334-MIND
          </a>
          <p className="text-steel text-sm" data-id="element-2007" data-mp="11yomhxm3m8z9">
            White Glove Onboarding $997. 3-month minimum term.
          </p>
        </div>
      </section>
    </main>;
}